import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, FileText, Calendar, Briefcase, Mail, Send,
  RefreshCw, Filter, CheckCircle, Clock, XCircle,
  ChevronDown, LogOut, Eye, Phone, MapPin,
  Edit2, Save, X, FileDown, CalendarDays, Home, Car, Wrench, Trash2, CheckSquare, Square
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service labels
const SERVICE_LABELS = {
  menage: 'Ménage',
  jardinage: 'Jardinage',
  bricolage: 'Bricolage',
  demenagement: 'Aide au déménagement',
  administratif: 'Aide administrative',
  vehicule: 'Nettoyage véhicule',
  airbnb: 'Gestion Airbnb'
};

// Type labels for unified contacts
const TYPE_LABELS = {
  contact: { label: 'Contact', color: 'bg-blue-100 text-blue-800' },
  devis: { label: 'Devis', color: 'bg-green-100 text-green-800' },
  rdv: { label: 'RDV', color: 'bg-purple-100 text-purple-800' },
  candidature: { label: 'Candidature', color: 'bg-orange-100 text-orange-800' },
  newsletter: { label: 'Newsletter', color: 'bg-pink-100 text-pink-800' }
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    nouveau: { color: 'bg-blue-100 text-blue-800', icon: Clock },
    nouvelle: { color: 'bg-blue-100 text-blue-800', icon: Clock },
    en_attente: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    en_cours: { color: 'bg-purple-100 text-purple-800', icon: RefreshCw },
    confirmé: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
    traité: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
    terminé: { color: 'bg-gray-100 text-gray-800', icon: CheckCircle },
    annulé: { color: 'bg-red-100 text-red-800', icon: XCircle },
    refusé: { color: 'bg-red-100 text-red-800', icon: XCircle }
  };
  
  const config = statusConfig[status] || statusConfig.nouveau;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="h-3 w-3" />
      {status}
    </span>
  );
};

// Status selector dropdown
const StatusSelector = ({ currentStatus, onStatusChange, itemId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const statuses = ['nouveau', 'en_attente', 'en_cours', 'traité', 'confirmé', 'terminé', 'annulé', 'refusé'];
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
      >
        <StatusBadge status={currentStatus} />
        <ChevronDown className="h-4 w-4" />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg border z-50 py-1 min-w-[140px]">
          {statuses.map(status => (
            <button
              key={status}
              onClick={() => {
                onStatusChange(itemId, status);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                status === currentStatus ? 'bg-gray-100' : ''
              }`}
            >
              <StatusBadge status={status} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Editable price component
const EditablePrice = ({ value, onSave, quoteId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [price, setPrice] = useState(value);
  
  const handleSave = () => {
    onSave(quoteId, parseFloat(price));
    setIsEditing(false);
  };
  
  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="w-20 h-7 text-sm"
        />
        <button onClick={handleSave} className="p-1 text-green-600 hover:bg-green-50 rounded">
          <Save className="h-4 w-4" />
        </button>
        <button onClick={() => setIsEditing(false)} className="p-1 text-red-600 hover:bg-red-50 rounded">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-1">
      <span className="font-semibold text-[#D4AF37]">{value}€</span>
      <button 
        onClick={() => setIsEditing(true)} 
        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
        title="Modifier le prix"
      >
        <Edit2 className="h-3 w-3" />
      </button>
    </div>
  );
};

// Quote detail modal
const QuoteDetailModal = ({ quote, onClose, authHeader }) => {
  if (!quote) return null;
  
  const renderServiceDetails = (service) => {
    const options = service.options || {};
    const details = [];
    
    if (options.hours) details.push({ label: 'Durée demandée', value: `${options.hours} heure(s)` });
    if (options.frequency) details.push({ label: 'Fréquence', value: options.frequency === 'regulier' ? 'Régulier' : 'Ponctuel' });
    if (options.cleaningType) {
      const types = { interieur: 'Intérieur', exterieur: 'Extérieur', complet: 'Complet' };
      details.push({ label: 'Type de nettoyage', value: types[options.cleaningType] || options.cleaningType });
    }
    if (options.vehicleType) {
      const types = { petit: 'Petit véhicule', standard: 'Véhicule standard', gros: 'Gros véhicule' };
      details.push({ label: 'Taille véhicule', value: types[options.vehicleType] || options.vehicleType });
    }
    if (options.vehicleBrand && options.vehicleModel) {
      details.push({ label: '🚗 Véhicule', value: `${options.vehicleBrand} ${options.vehicleModel}` });
    }
    if (options.hasAnimalHair) {
      details.push({ label: '🐾 Poils d\'animaux', value: 'Oui - présents dans le véhicule' });
    }
    if (options.selectedOptions?.length) details.push({ label: 'Options', value: options.selectedOptions.join(', ') });
    if (options.deepCleaningOptions?.length) details.push({ label: 'Nettoyage en profondeur', value: options.deepCleaningOptions.join(', ') });
    if (options.tasks?.length) details.push({ label: 'Travaux demandés', value: options.tasks.join(', ') });
    if (options.description) details.push({ label: 'Description', value: options.description });
    
    return details;
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-[#0A2320] text-white p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Devis de {quote.first_name} {quote.last_name}</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <a href={`mailto:${quote.email}`} className="text-blue-600 hover:underline">{quote.email}</a>
            </div>
            <div>
              <p className="text-sm text-gray-500">Téléphone</p>
              <a href={`tel:${quote.phone}`} className="text-gray-900 font-medium">{quote.phone}</a>
            </div>
            <div>
              <p className="text-sm text-gray-500">Adresse</p>
              <p className="text-gray-900">{quote.address}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Ville</p>
              <p className="text-gray-900">{quote.city}</p>
            </div>
          </div>
          
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-600 font-medium mb-1">Date et heure souhaitées</p>
            <p className="text-lg font-semibold text-blue-800">
              {quote.preferred_date || 'Non spécifiée'} {quote.preferred_time && ` à ${quote.preferred_time}`}
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#0A2320]" />
              Services demandés
            </h3>
            
            {quote.services?.map((service, idx) => (
              <div key={idx} className="mb-4 p-4 border rounded-lg bg-white shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  {service.service_id === 'menage' && <Home className="h-5 w-5 text-[#0A2320]" />}
                  {service.service_id === 'vehicule' && <Car className="h-5 w-5 text-[#0A2320]" />}
                  {service.service_id === 'bricolage' && <Wrench className="h-5 w-5 text-[#0A2320]" />}
                  <span className="font-semibold text-[#0A2320]">{SERVICE_LABELS[service.service_id] || service.service_id}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {renderServiceDetails(service).map((detail, i) => (
                    <div key={i} className="text-sm">
                      <span className="text-gray-500">{detail.label}:</span>
                      <span className="ml-2 text-gray-900">{detail.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {/* Photos section */}
          {quote.photo_ids && quote.photo_ids.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                📷 Photos jointes ({quote.photo_ids.length})
              </h3>
              <div className="grid grid-cols-3 gap-4">
                {quote.photo_ids.map((photoId, idx) => (
                  <a
                    key={idx}
                    href={`${API_URL}/api/files/${photoId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block aspect-square bg-gray-100 rounded-lg overflow-hidden hover:opacity-90 transition-opacity border"
                  >
                    <img
                      src={`${API_URL}/api/files/${photoId}`}
                      alt={`Photo ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentNode.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400 text-sm">Photo non disponible</div>';
                      }}
                    />
                  </a>
                ))}
              </div>
            </div>
          )}
          
          {quote.message && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Message</h3>
              <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{quote.message}</p>
            </div>
          )}
          
          <div className="p-4 bg-[#D4AF37]/10 rounded-lg border border-[#D4AF37]">
            <p className="text-sm text-[#0A2320] font-medium">Prix estimé</p>
            <p className="text-2xl font-bold text-[#D4AF37]">{quote.estimated_price}€</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Application detail modal
const ApplicationDetailModal = ({ application, onClose, authHeader }) => {
  const serviceLabels = {
    menage: 'Ménage / Entretien',
    jardinage: 'Jardinage',
    bricolage: 'Bricolage',
    administratif: 'Aide administrative'
  };
  
  const downloadCV = async () => {
    if (!application.cv_filename) return;
    
    try {
      const response = await fetch(`${API_URL}/api/files/${application.cv_filename}`, {
        headers: { 'Authorization': authHeader }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `CV_${application.first_name}_${application.last_name}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Erreur lors du téléchargement');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-[#0A2320] text-white p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-white">Candidature de {application.first_name} {application.last_name}</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <a href={`mailto:${application.email}`} className="text-blue-600 hover:underline">{application.email}</a>
            </div>
            <div>
              <p className="text-sm text-gray-500">Téléphone</p>
              <a href={`tel:${application.phone}`} className="text-gray-900">{application.phone}</a>
            </div>
            <div>
              <p className="text-sm text-gray-500">Ville</p>
              <p className="text-gray-900">{application.city}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Expérience</p>
              <p className="text-gray-900 capitalize">{application.experience?.replace('_', ' ')}</p>
            </div>
          </div>
          
          <div>
            <p className="text-sm text-gray-500 mb-2">Services intéressés</p>
            <div className="flex flex-wrap gap-2">
              {application.services?.map(service => (
                <span key={service} className="px-3 py-1 bg-[#0A2320] text-white rounded-full text-sm">
                  {serviceLabels[service] || service}
                </span>
              ))}
            </div>
          </div>
          
          <div>
            <p className="text-sm text-gray-500 mb-2 flex items-center gap-1">
              <CalendarDays className="h-4 w-4" />
              Disponibilités
            </p>
            <div className="flex flex-wrap gap-2">
              {application.availability?.split(', ').map(day => (
                <span key={day} className="px-3 py-1 bg-[#D4AF37] text-white rounded-full text-sm capitalize">{day}</span>
              ))}
              {(!application.availability || application.availability === 'non_specifie') && (
                <span className="text-gray-500 italic">Non spécifié</span>
              )}
            </div>
          </div>
          
          <div>
            <p className="text-sm text-gray-500 mb-2">Lettre de motivation</p>
            <div className="bg-gray-50 rounded-lg p-4 border">
              {application.motivation ? (
                <p className="text-gray-700 whitespace-pre-wrap">{application.motivation}</p>
              ) : (
                <p className="text-gray-400 italic">Aucune lettre de motivation fournie</p>
              )}
            </div>
          </div>
          
          <div>
            <p className="text-sm text-gray-500 mb-2">CV</p>
            {application.cv_filename ? (
              <Button onClick={downloadCV} className="bg-[#0A2320] hover:bg-[#153254] text-white">
                <FileDown className="h-4 w-4 mr-2" />
                Télécharger le CV
              </Button>
            ) : (
              <p className="text-gray-400 italic">Aucun CV fourni</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Today's requests modal
const TodayModal = ({ data, onClose, formatDate }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-[#D4AF37] text-white p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Demandes du jour</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {data.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Aucune demande aujourd'hui</p>
          ) : (
            <div className="space-y-4">
              {data.map((item, idx) => (
                <div key={idx} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${TYPE_LABELS[item.type]?.color}`}>
                        {TYPE_LABELS[item.type]?.label}
                      </span>
                      <p className="font-semibold mt-2">
                        {item.display_name || item.name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || item.email}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500">{formatDate(item.created_at || item.subscribed_at)}</span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    {item.email && <p><Mail className="h-3 w-3 inline mr-1" />{item.email}</p>}
                    {item.phone && <p><Phone className="h-3 w-3 inline mr-1" />{item.phone}</p>}
                    {item.city && <p><MapPin className="h-3 w-3 inline mr-1" />{item.city}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Newsletter Composer Modal
const NewsletterComposer = ({ subscribers, authHeader, onClose }) => {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [sending, setSending] = useState(false);

  const toggleAll = () => {
    if (selectedEmails.length === subscribers.length) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(subscribers.map(s => s.email));
    }
  };

  const handleSend = async () => {
    if (!subject || !content) {
      toast.error('Veuillez remplir le sujet et le contenu');
      return;
    }
    if (selectedEmails.length === 0) {
      toast.error('Sélectionnez au moins un destinataire');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter/send`, {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subject, content, recipients: selectedEmails })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Newsletter envoyée à ${result.sent_count} destinataire(s)`);
        onClose();
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-[#0A2320] text-white p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Envoyer une Newsletter</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sujet</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Objet de la newsletter"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contenu</label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Contenu de la newsletter..."
              rows={8}
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700">
                Destinataires ({selectedEmails.length}/{subscribers.length})
              </label>
              <button
                onClick={toggleAll}
                className="text-sm text-blue-600 hover:underline"
              >
                {selectedEmails.length === subscribers.length ? 'Désélectionner tout' : 'Tout sélectionner'}
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto border rounded-lg p-2">
              {subscribers.map(sub => (
                <label key={sub.id} className="flex items-center gap-2 p-1 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedEmails.includes(sub.email)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedEmails([...selectedEmails, sub.email]);
                      } else {
                        setSelectedEmails(selectedEmails.filter(email => email !== sub.email));
                      }
                    }}
                  />
                  <span className="text-sm">{sub.email}</span>
                </label>
              ))}
            </div>
          </div>

          <Button
            onClick={handleSend}
            disabled={sending}
            className="w-full bg-[#D4AF37] hover:bg-[#c9a030] text-white"
          >
            {sending ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Envoyer ({selectedEmails.length})
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// BlockedDatesManager — Gestion des dates bloquées (indispos)
// ============================================================
const BlockedDatesManager = ({ authHeader }) => {
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [viewYear, setViewYear] = useState(new Date().getFullYear());

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  const fetchBlocked = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/blocked-dates`, {
        headers: { 'Authorization': authHeader }
      });
      if (res.ok) {
        const data = await res.json();
        setBlockedDates(data);
      }
    } catch (e) {
      console.error('fetch blocked dates failed', e);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { fetchBlocked(); }, [fetchBlocked]);

  // On considère les créneaux standards (09:00, 13:00, 14:00) — l'admin peut bloquer l'un OU toute la journée
  const ALL_SLOTS = ['09:00', '13:00', '14:00'];

  // Maps pour accès rapide
  // fullDayBlocked: Set<date> — journées bloquées entièrement
  // slotBlocked: {date: Set<time>} — créneaux spécifiques bloqués
  const fullDayBlocked = new Set(blockedDates.filter(b => !b.time).map(b => b.date));
  const slotBlocked = {};
  blockedDates.forEach(b => {
    if (b.time) {
      if (!slotBlocked[b.date]) slotBlocked[b.date] = new Set();
      slotBlocked[b.date].add(b.time);
    }
  });

  const [modalDate, setModalDate] = useState(null);

  const formatDate = (y, m, d) =>
    `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const callApi = async (method, url, body) => {
    const opts = {
      method,
      headers: { 'Authorization': authHeader, 'Content-Type': 'application/json' }
    };
    if (body) opts.body = JSON.stringify(body);
    const res = await fetch(url, opts);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  const toggleFullDay = async (dateStr) => {
    try {
      if (fullDayBlocked.has(dateStr)) {
        await callApi('DELETE', `${API_URL}/api/admin/blocked-dates/${dateStr}`);
        toast.success(`Journée ${dateStr} débloquée`);
      } else {
        await callApi('POST', `${API_URL}/api/admin/blocked-dates`, {
          dates: [dateStr], reason: 'Journée bloquée'
        });
        toast.success(`Journée ${dateStr} bloquée`);
      }
      fetchBlocked();
    } catch (e) { toast.error(e.message); }
  };

  const toggleSlot = async (dateStr, time) => {
    try {
      const isBlocked = slotBlocked[dateStr]?.has(time);
      if (isBlocked) {
        await callApi('DELETE', `${API_URL}/api/admin/blocked-dates/${dateStr}?time=${encodeURIComponent(time)}`);
        toast.success(`Créneau ${dateStr} ${time} débloqué`);
      } else {
        await callApi('POST', `${API_URL}/api/admin/blocked-dates`, {
          dates: [dateStr], time, reason: 'Créneau bloqué'
        });
        toast.success(`Créneau ${dateStr} ${time} bloqué`);
      }
      fetchBlocked();
    } catch (e) { toast.error(e.message); }
  };

  const firstDay = new Date(viewYear, viewMonth, 1);
  const lastDay = new Date(viewYear, viewMonth + 1, 0);
  const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  const daysInMonth = lastDay.getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  return (
    <div className="bg-white rounded-xl shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-[#153254] flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Gestion du planning
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Cliquez sur une date pour gérer ses créneaux (09:00, 13:00, 14:00) ou bloquer toute la journée. Les dimanches sont automatiquement indisponibles.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchBlocked} disabled={loading} data-testid="refresh-blocked-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="prev-month-btn">
          ← {monthNames[(viewMonth + 11) % 12]}
        </Button>
        <h3 className="text-lg font-semibold text-[#153254]" data-testid="current-month-label">
          {monthNames[viewMonth]} {viewYear}
        </h3>
        <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="next-month-btn">
          {monthNames[(viewMonth + 1) % 12]} →
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'].map((d) => (
          <div key={d} className="text-center text-xs font-semibold text-gray-500 py-2">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {[...Array(startPadding)].map((_, i) => (
          <div key={`pad-${i}`} className="h-14" />
        ))}
        {[...Array(daysInMonth)].map((_, i) => {
          const day = i + 1;
          const dateStr = formatDate(viewYear, viewMonth, day);
          const d = new Date(viewYear, viewMonth, day);
          const isPast = d < today;
          const isSunday = d.getDay() === 0;
          const isFullBlocked = fullDayBlocked.has(dateStr);
          const blockedCount = slotBlocked[dateStr]?.size || 0;
          const isClickable = !isPast && !isSunday;
          let bg = 'bg-green-50 hover:bg-green-100 text-green-800 border-green-200';
          let label = 'Dispo';
          if (isPast) { bg = 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'; label = ''; }
          else if (isSunday) { bg = 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'; label = 'Fermé'; }
          else if (isFullBlocked) { bg = 'bg-red-100 hover:bg-red-200 text-red-800 border-red-300'; label = 'Bloqué'; }
          else if (blockedCount > 0) { bg = 'bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-300'; label = `${blockedCount} pris`; }
          return (
            <button
              key={day}
              onClick={() => isClickable && setModalDate(dateStr)}
              disabled={!isClickable}
              data-testid={`cal-day-${dateStr}`}
              className={`h-14 rounded-lg border text-sm font-medium transition-all flex flex-col items-center justify-center ${bg}`}
              title={isClickable ? `Cliquer pour gérer les créneaux` : ''}
            >
              <span className="text-base font-bold">{day}</span>
              {label && <span className="text-[10px] uppercase tracking-wider">{label}</span>}
            </button>
          );
        })}
      </div>

      {/* Modal de gestion des créneaux */}
      {modalDate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setModalDate(null)}>
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="slot-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#153254]">
                Gérer {modalDate}
              </h3>
              <button onClick={() => setModalDate(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className={`border rounded-lg p-3 ${fullDayBlocked.has(modalDate) ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-200'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fullDayBlocked.has(modalDate)}
                    onChange={() => toggleFullDay(modalDate)}
                    className="mt-1 w-4 h-4 rounded"
                    data-testid="toggle-full-day"
                  />
                  <div>
                    <p className="font-semibold text-[#153254]">Bloquer toute la journée</p>
                    <p className="text-xs text-gray-500 mt-1">Aucun service ne pourra être réservé ce jour-là.</p>
                  </div>
                </label>
              </div>

              <div className={`${fullDayBlocked.has(modalDate) ? 'opacity-40 pointer-events-none' : ''}`}>
                <p className="text-sm font-semibold text-[#153254] mb-2">Ou bloquer des créneaux précis :</p>
                <div className="grid grid-cols-3 gap-2">
                  {ALL_SLOTS.map(slot => {
                    const isBlocked = slotBlocked[modalDate]?.has(slot);
                    return (
                      <button
                        key={slot}
                        onClick={() => toggleSlot(modalDate, slot)}
                        data-testid={`toggle-slot-${slot}`}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                          isBlocked
                            ? 'bg-red-100 border-red-300 text-red-800'
                            : 'bg-green-50 border-green-200 text-green-800 hover:bg-green-100'
                        }`}
                      >
                        {slot}
                        <br />
                        <span className="text-[10px] uppercase">{isBlocked ? 'Bloqué' : 'Dispo'}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Astuce : bloque 09:00 si tu as déjà un RDV à ce créneau mais que 13:00 ou 14:00 restent dispo.
                </p>
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <Button onClick={() => setModalDate(null)} size="sm" data-testid="close-modal-btn">
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-4 text-xs text-gray-600">
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-green-100 border border-green-300" /> Disponible</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300" /> Créneau(x) pris</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-red-100 border border-red-300" /> Journée bloquée</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-gray-100 border border-gray-200" /> Fermé / Passé</span>
      </div>

      <div className="mt-6 border-t pt-4">
        <h4 className="text-sm font-semibold text-[#153254] mb-2">
          Dates & créneaux bloqués ({blockedDates.length})
        </h4>
        {blockedDates.length === 0 ? (
          <p className="text-sm text-gray-500">Aucune date bloquée.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {blockedDates.map(b => (
              <span
                key={`${b.date}-${b.time || 'all'}`}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm border ${
                  b.time
                    ? 'bg-amber-50 text-amber-800 border-amber-300'
                    : 'bg-red-50 text-red-800 border-red-200'
                }`}
                data-testid={`blocked-chip-${b.date}-${b.time || 'all'}`}
              >
                {b.date}{b.time ? ` · ${b.time}` : ' · toute la journée'}
                <button
                  onClick={() => b.time ? toggleSlot(b.date, b.time) : toggleFullDay(b.date)}
                  className="hover:opacity-80"
                  aria-label={`Débloquer ${b.date} ${b.time || ''}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};



export const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authHeader, setAuthHeader] = useState('');
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({
    contacts: [],
    quotes: [],
    appointments: [],
    applications: [],
    newsletter: [],
    allContacts: [],
    today: []
  });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [showNewsletterComposer, setShowNewsletterComposer] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showTodayModal, setShowTodayModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const createAuthHeader = (username, password) => {
    return 'Basic ' + btoa(`${username}:${password}`);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const header = createAuthHeader(credentials.username, credentials.password);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': header }
      });
      
      if (response.ok) {
        setAuthHeader(header);
        setIsAuthenticated(true);
        const statsData = await response.json();
        setStats(statsData);
        toast.success('Connexion réussie');
      } else {
        toast.error('Identifiants incorrects');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthHeader('');
    setCredentials({ username: '', password: '' });
    setActiveTab('stats');
    toast.success('Déconnexion réussie');
  };

  const fetchData = useCallback(async (tab) => {
    if (!authHeader) return;
    
    setLoading(true);
    try {
      let endpoint = '';
      switch (tab) {
        case 'contacts': endpoint = '/api/admin/all-contacts'; break;
        case 'quotes': endpoint = '/api/admin/quotes'; break;
        case 'appointments': endpoint = '/api/admin/appointments'; break;
        case 'applications': endpoint = '/api/admin/candidatures'; break;
        case 'newsletter': endpoint = '/api/admin/newsletter'; break;
        default: return;
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': authHeader }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(prev => ({ ...prev, [tab]: result }));
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
      setSelectedItems([]);
    }
  }, [authHeader]);

  const fetchStats = useCallback(async () => {
    if (!authHeader) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': authHeader }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [authHeader]);

  const fetchTodayData = async () => {
    if (!authHeader) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/today`, {
        headers: { 'Authorization': authHeader }
      });
      if (response.ok) {
        const todayData = await response.json();
        setData(prev => ({ ...prev, today: todayData }));
        setShowTodayModal(true);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const updateStatus = async (type, id, status) => {
    try {
      let endpoint = '';
      // For unified contacts tab, we need to determine the actual type
      if (type === 'contacts') {
        // Find the item to get its actual type
        const item = data.contacts.find(c => c.id === id);
        if (item) {
          switch (item.type) {
            case 'contact': endpoint = `/api/admin/contacts/${id}/status`; break;
            case 'devis': endpoint = `/api/admin/quotes/${id}/status`; break;
            case 'rdv': endpoint = `/api/admin/appointments/${id}/status`; break;
            case 'candidature': endpoint = `/api/admin/candidatures/${id}/status`; break;
            default: endpoint = `/api/admin/contacts/${id}/status`;
          }
        } else {
          endpoint = `/api/admin/contacts/${id}/status`;
        }
      } else {
        switch (type) {
          case 'quotes': endpoint = `/api/admin/quotes/${id}/status`; break;
          case 'appointments': endpoint = `/api/admin/appointments/${id}/status`; break;
          case 'applications': endpoint = `/api/admin/candidatures/${id}/status`; break;
          default: return;
        }
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      
      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchData(type);
        fetchStats();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const updateQuotePrice = async (quoteId, newPrice) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${quoteId}/price`, {
        method: 'PUT',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ estimated_price: newPrice })
      });
      
      if (response.ok) {
        toast.success('Prix mis à jour');
        fetchData('quotes');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const deleteSelected = async () => {
    if (selectedItems.length === 0) return;
    
    if (!window.confirm(`Supprimer ${selectedItems.length} élément(s) ?`)) return;
    
    try {
      let endpoint = '';
      switch (activeTab) {
        case 'quotes': endpoint = '/api/admin/quotes/delete-bulk'; break;
        case 'appointments': endpoint = '/api/admin/appointments/delete-bulk'; break;
        case 'applications': endpoint = '/api/admin/candidatures/delete-bulk'; break;
        case 'contacts': endpoint = '/api/admin/contacts/delete-bulk'; break;
        case 'newsletter': endpoint = '/api/admin/newsletter/delete-bulk'; break;
        default: return;
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: selectedItems })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchData(activeTab);
        fetchStats();
        setSelectedItems([]);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleSelectAll = () => {
    const currentData = filteredData || [];
    if (selectedItems.length === currentData.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(currentData.map(item => item.id));
    }
  };

  const toggleSelectItem = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(i => i !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  useEffect(() => {
    if (isAuthenticated && activeTab !== 'stats' && activeTab !== 'calendar') {
      fetchData(activeTab);
    }
  }, [activeTab, isAuthenticated, fetchData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getServiceDetails = (services) => {
    if (!services || !Array.isArray(services)) return '-';
    return services.map(s => {
      let detail = s.service_id || s;
      if (s.options) {
        const opts = [];
        if (s.options.hours) opts.push(`${s.options.hours}h`);
        if (opts.length) detail += ` (${opts.join(' - ')})`;
      }
      return detail;
    }).join(' | ');
  };

  const filteredData = statusFilter
    ? data[activeTab]?.filter(item => item.status === statusFilter)
    : data[activeTab];

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A2320] to-[#153254] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-[#153254]">Dashboard Admin</h1>
            <p className="text-gray-500">The MajordHome Conciergerie</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Identifiant</label>
              <Input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                placeholder="admin"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
              <Input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                placeholder="••••••••"
                required
              />
            </div>
            <Button type="submit" className="w-full bg-[#0A2320] hover:bg-[#0d2e2a] text-white py-3">
              Se connecter
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0A2320] text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-white">Dashboard Admin</h1>
            <p className="text-sm text-white/70">The MajordHome Conciergerie</p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleLogout}
            className="border-white text-white hover:bg-white hover:text-[#0A2320]"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 bg-white rounded-lg p-2 shadow">
          {[
            { id: 'stats', label: 'Statistiques', icon: Eye },
            { id: 'contacts', label: 'Contacts', icon: Users },
            { id: 'quotes', label: 'Devis', icon: FileText },
            { id: 'appointments', label: 'Rendez-vous', icon: Calendar },
            { id: 'applications', label: 'Candidatures', icon: Briefcase },
            { id: 'newsletter', label: 'Newsletter', icon: Mail },
            { id: 'calendar', label: 'Calendrier', icon: CalendarDays }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setStatusFilter(''); setSelectedItems([]); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#0A2320] text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              onClick={() => setActiveTab('contacts')}
              className="bg-white rounded-xl p-6 shadow hover:shadow-lg transition-shadow text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_contacts}</p>
                  <p className="text-sm text-gray-500">Contacts</p>
                </div>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('quotes')}
              className="bg-white rounded-xl p-6 shadow hover:shadow-lg transition-shadow text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <FileText className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_quotes}</p>
                  <p className="text-sm text-gray-500">Devis ({stats.pending_quotes} en attente)</p>
                </div>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('appointments')}
              className="bg-white rounded-xl p-6 shadow hover:shadow-lg transition-shadow text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Calendar className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_appointments}</p>
                  <p className="text-sm text-gray-500">Rendez-vous</p>
                </div>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className="bg-white rounded-xl p-6 shadow hover:shadow-lg transition-shadow text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Briefcase className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_applications}</p>
                  <p className="text-sm text-gray-500">Candidatures</p>
                </div>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('newsletter')}
              className="bg-white rounded-xl p-6 shadow hover:shadow-lg transition-shadow text-left col-span-2"
            >
              <div className="flex items-center gap-3">
                <div className="p-3 bg-pink-100 rounded-lg">
                  <Mail className="h-6 w-6 text-pink-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_newsletter}</p>
                  <p className="text-sm text-gray-500">Abonnés newsletter</p>
                </div>
              </div>
            </button>
            <button
              onClick={fetchTodayData}
              className="bg-gradient-to-r from-[#D4AF37] to-[#c9a030] rounded-xl p-6 shadow col-span-2 text-white text-left hover:shadow-lg transition-shadow"
            >
              <p className="text-4xl font-bold">{stats.new_today}</p>
              <p className="text-sm opacity-90">Nouvelles demandes aujourd'hui</p>
              <p className="text-xs opacity-75 mt-2">Cliquez pour voir les détails</p>
            </button>
          </div>
        )}

        {/* Calendar Tab — Manage blocked dates */}
        {activeTab === 'calendar' && (
          <BlockedDatesManager authHeader={authHeader} />
        )}

        {/* Data Lists */}
        {activeTab !== 'stats' && activeTab !== 'calendar' && (
          <div className="bg-white rounded-xl shadow">
            {/* Filter bar */}
            <div className="p-4 border-b flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border rounded-lg px-3 py-1.5 text-sm"
                >
                  <option value="">Tous les statuts</option>
                  <option value="nouveau">Nouveau</option>
                  <option value="en_attente">En attente</option>
                  <option value="en_cours">En cours</option>
                  <option value="traité">Traité</option>
                  <option value="confirmé">Confirmé</option>
                  <option value="terminé">Terminé</option>
                  <option value="annulé">Annulé</option>
                </select>
                
                {selectedItems.length > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={deleteSelected}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Supprimer ({selectedItems.length})
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {activeTab === 'newsletter' && (
                  <Button
                    onClick={() => setShowNewsletterComposer(true)}
                    className="bg-[#D4AF37] hover:bg-[#c9a030] text-white"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Envoyer une newsletter
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(activeTab)}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
              </div>
            </div>

            {/* Data table */}
            <div className="overflow-y-auto max-h-[calc(100vh-280px)]">
              {loading ? (
                <div className="p-8 text-center text-gray-500">
                  <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                  Chargement...
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-3 text-left w-10">
                        <button onClick={toggleSelectAll} className="p-1 hover:bg-gray-200 rounded">
                          {selectedItems.length === (filteredData?.length || 0) && filteredData?.length > 0 ? (
                            <CheckSquare className="h-4 w-4 text-[#0A2320]" />
                          ) : (
                            <Square className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-28">Date</th>
                      {activeTab === 'contacts' && (
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">Type</th>
                      )}
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-36">Nom</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-48">Contact</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Détails</th>
                      {activeTab === 'quotes' && (
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-20">Prix</th>
                      )}
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-28">
                        {activeTab === 'newsletter' ? 'État' : 'Statut'}
                      </th>
                      {(activeTab === 'applications' || activeTab === 'quotes') && (
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredData?.map((item, idx) => (
                      <tr key={item.id || idx} className={`hover:bg-gray-50 ${selectedItems.includes(item.id) ? 'bg-blue-50' : ''}`}>
                        <td className="px-3 py-3 w-10">
                          <button 
                            onClick={() => toggleSelectItem(item.id)} 
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            {selectedItems.includes(item.id) ? (
                              <CheckSquare className="h-4 w-4 text-[#0A2320]" />
                            ) : (
                              <Square className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-600 w-28">
                          {formatDate(item.created_at || item.subscribed_at || item.date)}
                        </td>
                        {activeTab === 'contacts' && (
                          <td className="px-3 py-3 w-24">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${TYPE_LABELS[item.type]?.color || 'bg-gray-100'}`}>
                              {TYPE_LABELS[item.type]?.label || item.type}
                            </span>
                          </td>
                        )}
                        <td className="px-3 py-3 w-36">
                          <p className="font-medium text-gray-900 truncate">
                            {item.display_name && item.display_name !== 'undefined undefined' 
                              ? item.display_name
                              : (item.first_name && item.last_name 
                                ? `${item.first_name} ${item.last_name}`
                                : item.name || item.email || '-')}
                          </p>
                          {item.city && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 truncate">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              {item.city}
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-3 text-sm w-48">
                          <div className="flex flex-col gap-1">
                            <a href={`mailto:${item.email}`} className="text-blue-600 hover:underline flex items-center gap-1 truncate">
                              <Mail className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{item.email}</span>
                            </a>
                            {item.phone && (
                              <a href={`tel:${item.phone}`} className="text-gray-600 flex items-center gap-1">
                                <Phone className="h-3 w-3 flex-shrink-0" />
                                {item.phone}
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-600">
                          {activeTab === 'contacts' && (
                            <div className="truncate">
                              <p className="font-medium">{item.service || '-'}</p>
                              {item.message && <p className="text-xs text-gray-500 truncate">{item.message}</p>}
                            </div>
                          )}
                          {activeTab === 'quotes' && (
                            <div>
                              <p className="text-xs truncate">{getServiceDetails(item.services)}</p>
                              {item.preferred_date && (
                                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                  <Calendar className="h-3 w-3 flex-shrink-0" />
                                  {item.preferred_date} {item.preferred_time && `à ${item.preferred_time}`}
                                </p>
                              )}
                            </div>
                          )}
                          {activeTab === 'appointments' && (
                            <span>{item.date} à {item.time}</span>
                          )}
                          {activeTab === 'applications' && (
                            <div>
                              <p className="text-xs truncate">{item.services?.join(', ')}</p>
                              {item.availability && item.availability !== 'non_specifie' && (
                                <p className="text-xs text-[#D4AF37] mt-1 flex items-center gap-1">
                                  <CalendarDays className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate">{item.availability}</span>
                                </p>
                              )}
                            </div>
                          )}
                          {activeTab === 'newsletter' && (
                            <span className="truncate">{item.preferences?.join(', ') || 'Tous services'}</span>
                          )}
                        </td>
                        {activeTab === 'quotes' && (
                          <td className="px-3 py-3 w-20">
                            <EditablePrice 
                              value={item.estimated_price} 
                              onSave={updateQuotePrice}
                              quoteId={item.id}
                            />
                          </td>
                        )}
                        <td className="px-3 py-3 w-28">
                          {activeTab !== 'newsletter' ? (
                            <StatusSelector
                              currentStatus={item.status || 'nouveau'}
                              onStatusChange={(id, status) => updateStatus(activeTab, id, status)}
                              itemId={item.id}
                            />
                          ) : (
                            <span className={`px-2 py-1 rounded-full text-xs ${item.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                              {item.is_active ? 'Actif' : 'Inactif'}
                            </span>
                          )}
                        </td>
                        {activeTab === 'applications' && (
                          <td className="px-3 py-3 w-24">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedApplication(item)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Voir
                            </Button>
                          </td>
                        )}
                        {activeTab === 'quotes' && (
                          <td className="px-3 py-3 w-24">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedQuote(item)}
                              className="border-[#0A2320] text-[#0A2320] hover:bg-[#0A2320] hover:text-white"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Détails
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                    
                    {(!filteredData || filteredData.length === 0) && (
                      <tr>
                        <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                          Aucune donnée trouvée
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Newsletter Composer Modal */}
      {showNewsletterComposer && (
        <NewsletterComposer
          subscribers={data.newsletter}
          authHeader={authHeader}
          onClose={() => setShowNewsletterComposer(false)}
        />
      )}

      {/* Application Detail Modal */}
      {selectedApplication && (
        <ApplicationDetailModal
          application={selectedApplication}
          authHeader={authHeader}
          onClose={() => setSelectedApplication(null)}
        />
      )}

      {/* Quote Detail Modal */}
      {selectedQuote && (
        <QuoteDetailModal
          quote={selectedQuote}
          authHeader={authHeader}
          onClose={() => setSelectedQuote(null)}
        />
      )}

      {/* Today Modal */}
      {showTodayModal && (
        <TodayModal
          data={data.today}
          onClose={() => setShowTodayModal(false)}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};
